import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import './Monitoring.css';
import Menu from '../Shared/Menu';
import usePageTitle from '../Shared/usePageTitle';
import { SUBSCRIBER_HEALTH } from '../../data/monitoringData';

const D = SUBSCRIBER_HEALTH;

const KPIS = [
    {
        target: 'shInactive', cardClass: 'sh-kpi-active', iconClass: 'sh-icon-active', valueClass: 'sh-kpi-value-active',
        label: 'Total Active Subscribers', value: D.kpis.active.value, subtext: D.kpis.active.subtext,
        icon: <><circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" /></>,
    },
    {
        target: 'shBounce', cardClass: 'sh-kpi-bounce', iconClass: 'sh-icon-bounce', valueClass: 'sh-kpi-value-bounce',
        label: 'Hard Bounced', value: D.kpis.hardBounced.value, subtext: D.kpis.hardBounced.subtext,
        icon: <><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></>,
    },
    {
        target: 'shUnsub', cardClass: 'sh-kpi-unsub', iconClass: 'sh-icon-unsub', valueClass: 'sh-kpi-value-unsub',
        label: 'Unsubscribed', value: D.kpis.unsubscribed.value, subtext: D.kpis.unsubscribed.subtext,
        icon: <><path d="M9 11l-4 4 4 4" /><path d="M5 15h11a4 4 0 0 0 0-8h-1" /></>,
    },
    {
        target: 'shSuppress', cardClass: 'sh-kpi-suppress', iconClass: 'sh-icon-suppress', valueClass: 'sh-kpi-value-suppress',
        label: 'On Suppression List', value: D.kpis.suppressed.value, subtext: D.kpis.suppressed.subtext,
        icon: <><circle cx="12" cy="12" r="10" /><path d="M4.93 4.93l14.14 14.14" /></>,
    },
];

function SubscriberHealth() {
    usePageTitle('Subscriber Health');

    const [selected, setSelected] = useState(null);

    const scrollTo = id => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const totalSuppressed = D.suppressionLists.length;

    return (
        <>
            <Menu />

            <div className="container fade-up container-main">
                <div className="page-header mb-4">
                    <h1>Subscriber Health</h1>
                    <p>Active status, bounces, unsubscribes, and suppression across the All Subscribers list</p>
                </div>

                <div className="row g-3 mb-4">
                    {KPIS.map(kpi => (
                        <div className="col-6 col-md-3" key={kpi.target}>
                            <button
                                type="button"
                                className={`kpi-card ah-kpi-card ${kpi.cardClass} d-flex align-items-start gap-3`}
                                onClick={() => scrollTo(kpi.target)}
                            >
                                <div className={`kpi-icon ${kpi.iconClass}`}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        {kpi.icon}
                                    </svg>
                                </div>
                                <div>
                                    <p className="kpi-label">{kpi.label}</p>
                                    <p className={`kpi-value ${kpi.valueClass}`}>{kpi.value}</p>
                                    <p className="kpi-subtext">{kpi.subtext}</p>
                                </div>
                            </button>
                        </div>
                    ))}
                </div>

                <div className="row g-3">
                    {/* Bounce breakdown */}
                    <div className="col-12 col-lg-6">
                        <div className="sh-section" id="shBounce">
                            <p className="sh-section-title">Bounce breakdown</p>
                            <p className="sh-section-sub">
                                Hard vs soft bounces as a share of {D.totalSubscribers} total subscribers
                            </p>
                            <div className="sh-bounce-track" aria-hidden="true">
                                <span className="sh-bounce-hard" style={{ width: `${D.bounces.hardWidth}%` }} />
                                <span className="sh-bounce-soft" style={{ width: `${D.bounces.softWidth}%` }} />
                            </div>
                            <div className="d-flex flex-column gap-3">
                                {D.bounces.rows.map(row => (
                                    <div className="sh-stat-row" key={row.label}>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="sh-legend-dot" style={{ background: row.color }} />
                                            <span className="sh-stat-name">{row.label}</span>
                                        </div>
                                        <div className="text-end">
                                            <p className="sh-stat-count">{row.count}</p>
                                            <p className="sh-stat-pct">{row.pct}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Unsubscribe trend */}
                    <div className="col-12 col-lg-6">
                        <div className="sh-section" id="shUnsub">
                            <p className="sh-section-title">Unsubscribe trend</p>
                            <p className="sh-section-sub">New unsubscribes over the last 30, 60, and 90 days</p>
                            <div className="d-flex flex-column gap-3">
                                {D.unsubTrend.map(row => (
                                    <div className="sh-trend-row" key={row.period}>
                                        <div className="sh-trend-label">
                                            <p className="sh-trend-period">{row.period}</p>
                                            <p className="sh-trend-count">{row.count}</p>
                                        </div>
                                        <div className="sh-trend-bar-wrap">
                                            <div className="sh-trend-bar" style={{ width: `${row.width}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Suppression lists */}
                    <div className="col-12 col-lg-6">
                        <div className="sh-section" id="shSuppress">
                            <p className="sh-section-title">Suppression lists</p>
                            <p className="sh-section-sub">
                                Click a list to see details · {totalSuppressed} lists · {D.kpis.suppressed.value} subscribers
                            </p>
                            <div className="d-flex flex-column gap-2">
                                {D.suppressionLists.map(list => (
                                    <button
                                        type="button"
                                        key={list.name}
                                        className="email-row-card ah-row sh-list-row"
                                        onClick={() => setSelected(list)}
                                    >
                                        <div className="email-accent-stripe" style={{ background: list.stripe }} />
                                        <div className="email-card-content">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                    <h3 className="email-card-title mb-1">{list.name}</h3>
                                                    <p className="email-meta-text mb-2">{list.folder} · {list.used}</p>
                                                    <div className="email-metrics-row">
                                                        <div className="email-metric-item">
                                                            <p className="email-metric-label">Subscribers</p>
                                                            <p className="email-metric-value"
                                                               style={list.countColor ? { color: list.countColor } : undefined}>
                                                                {list.count}
                                                            </p>
                                                        </div>
                                                        <div className="email-metric-item">
                                                            <p className="email-metric-label">Last modified</p>
                                                            <p className="email-metric-value">{list.modified}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <span className="ah-chevron" aria-hidden="true">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                         stroke="currentColor" strokeWidth="2.5">
                                                        <path d="M9 6l6 6-6 6" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Inactive subscribers */}
                    <div className="col-12 col-lg-6">
                        <div className="sh-section" id="shInactive">
                            <p className="sh-section-title">Inactive subscribers</p>
                            <p className="sh-section-sub">No open or click on any email in the last 90 days</p>
                            <div className="d-flex align-items-end justify-content-between gap-3 mb-3 flex-wrap">
                                <div>
                                    <p className="sh-inactive-count">{D.inactive.count}</p>
                                    <p className="sh-inactive-caption">{D.inactive.caption}</p>
                                </div>
                                <div className="sh-inactive-pct-wrap">
                                    <p className="sh-inactive-pct">{D.inactive.pct}</p>
                                    <p className="sh-inactive-caption mb-0">of total</p>
                                </div>
                            </div>
                            <div className="progress-bar-custom sh-inactive-bar">
                                <div className="progress-bar-fill"
                                     style={{ width: `${D.inactive.barWidth}%`, background: '#F59E0B' }} />
                            </div>
                            <p className="sh-inactive-note">{D.inactive.note}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Suppression list panel */}
            <div className={`ah-panel-backdrop${selected ? ' show' : ''}`} onClick={() => setSelected(null)} />
            <div className={`ah-panel-slide${selected ? ' show' : ''}`} data-ah-status="Draft">
                {selected && (
                    <>
                        <div className="ah-panel-hero">
                            <button type="button" className="ah-panel-close"
                                    onClick={() => setSelected(null)} aria-label="Close">✕</button>
                            <div className="d-flex align-items-center gap-3">
                                <div className="ah-panel-hero-icon">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                                         stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <path d="M4.93 4.93l14.14 14.14" />
                                    </svg>
                                </div>
                                <div className="ah-panel-hero-copy">
                                    <span className="ah-badge ah-badge-draft mb-2">
                                        <span className="ah-badge-dot" />Suppression list
                                    </span>
                                    <h2 className="ah-panel-title">{selected.name}</h2>
                                    <p className="ah-panel-hero-sub">{selected.folder}</p>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-body">
                            <p className="ah-panel-section">List snapshot</p>
                            <div className="ah-panel-metrics mb-4">
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#8B5CF614', color: '#7C3AED' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="8" r="4" /><path d="M4 20a8 8 0 0 1 16 0" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Subscribers</p>
                                        <p className="ah-panel-metric-value">{selected.count}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#0EA5E914', color: '#0284C7' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Last modified</p>
                                        <p className="ah-panel-metric-value">{selected.modified}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#6366F114', color: '#4F46E5' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">List type</p>
                                        <p className="ah-panel-metric-value">{selected.type}</p>
                                    </div>
                                </div>
                                <div className="ah-panel-metric">
                                    <div className="ah-panel-metric-icon" style={{ background: '#10B98114', color: '#059669' }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M4 4h16v6H4zM4 14h16v6H4z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="ah-panel-metric-label">Used by</p>
                                        <p className="ah-panel-metric-value">{selected.used}</p>
                                    </div>
                                </div>
                            </div>

                            <p className="ah-panel-section">Details</p>
                            <div className="ah-panel-details mb-4">
                                <div className="ah-panel-detail-row">
                                    <span className="ah-panel-detail-label">Folder path</span>
                                    <span className="ah-panel-detail-value">{selected.folder}</span>
                                </div>
                            </div>
                        </div>

                        <div className="ah-panel-footer">
                            <NavLink className="ah-open-btn" to="/data-extensions">
                                Open in Data Extensions
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2.5">
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </NavLink>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default SubscriberHealth;
